import type { ReactElement } from "react";
import type { TaskBucket } from "@playbookai/types";
import type { MyTasksParams } from "@playbookai/widget-schemas";
import { Checkbox } from "../../atoms/Checkbox/Checkbox.js";
import type { DashboardWidgetEditorProps } from "../types.js";

const LEGEND =
  "block text-dense-2 uppercase tracking-wider text-pb-text-muted font-semibold mb-1.5";

const RANKINGS = [
  { id: "ai", label: "AI ranked", hint: "Grouped into Do-today / Can-wait." },
  { id: "flat", label: "Flat list", hint: "One simple list, no grouping." },
] as const;

const BUCKETS: { id: TaskBucket; label: string; hint: string }[] = [
  { id: "today", label: "Do today", hint: "urgent + due now" },
  { id: "later", label: "Can wait", hint: "lower priority" },
];

/**
 * `myTasks` editor — default ranking (AI vs flat) + which priority buckets show.
 * No catalogue dependency (tasks are host-resolved); pure params editing.
 */
export function MyTasksEditor({
  draftParams,
  onChange,
}: DashboardWidgetEditorProps): ReactElement {
  const params = draftParams as MyTasksParams;
  const mode = params.mode ?? "ai";
  const priorities = new Set<TaskBucket>(
    params.priorities ?? ["today", "later"],
  );

  const togglePriority = (b: TaskBucket): void => {
    const next = new Set(priorities);
    if (next.has(b)) next.delete(b);
    else next.add(b);
    onChange({ ...params, priorities: [...next] });
  };

  return (
    <div className="space-y-4">
      <fieldset>
        <legend className={LEGEND}>Default ranking</legend>
        <div
          role="radiogroup"
          aria-label="Default ranking"
          className="space-y-1.5"
        >
          {RANKINGS.map((r) => (
            <label key={r.id} className="flex items-start gap-2 cursor-pointer">
              {/* eslint-disable-next-line ds/no-hand-built -- kept: a native radio in a two-line option; RadioGroup draws its own teal mark, and no approved look row lists the change. */}
              <input
                type="radio"
                name="pb-mytasks-mode"
                checked={mode === r.id}
                onChange={() => onChange({ ...params, mode: r.id })}
                className="mt-0.5 accent-pb-purple"
              />
              <span>
                <span className="block text-sm text-pb-text">{r.label}</span>
                <span className="block text-dense-1 text-pb-text-muted">
                  {r.hint}
                </span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="space-y-1.5">
        <legend className={LEGEND}>Priorities</legend>
        {BUCKETS.map((b) => (
          <Checkbox
            key={b.id}
            checked={priorities.has(b.id)}
            onChange={() => togglePriority(b.id)}
            label={
              <span>
                <span className="text-sm text-pb-text">{b.label}</span>
                <span className="text-dense-1 text-pb-text-muted">
                  {" "}
                  — {b.hint}
                </span>
              </span>
            }
          />
        ))}
        <p className="text-dense-1 text-pb-text-muted">
          Hide a bucket by unchecking it.
        </p>
      </fieldset>
    </div>
  );
}
